import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, ApiError, type Operator } from "../api";
import { useI18n, type Lang } from "../lib/i18n";
import { apiErrorText } from "../lib/apiError";

/**
 * Sign-in, including first-time enrolment.
 *
 * The flow mirrors the server exactly: password, then second factor, and there
 * is no branch in this component that reaches the console without the second
 * step — because there is no branch on the server that would honour one.
 * (Local development can switch the second step off on the server; see
 * CHAT_DEV_SKIP_2FA.)
 */

type Stage =
  | { name: "password" }
  | { name: "forgot"; sent: boolean }
  | { name: "code"; ticket: string }
  | { name: "enrol"; ticket: string; secret: string; qr: string }
  | { name: "codes"; codes: string[] };

export function Login({
  onSignedIn,
  onLang,
}: {
  onSignedIn: (operator: Operator) => void;
  onLang: (lang: Lang) => void;
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.login;
  const [stage, setStage] = useState<Stage>({ name: "password" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? apiErrorText(err, all, all.common.unexpected) : all.common.unexpected);
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = (event: Event): void => {
    event.preventDefault();
    void run(async () => {
      const res = await api.login(email, password);
      // Local development with CHAT_DEV_SKIP_2FA: the server signed us in already.
      if ("operator" in res) {
        onSignedIn(res.operator);
        return;
      }
      if (res.needsTwoFactorSetup) {
        const setup = await api.setupTwoFactor(res.ticket);
        setStage({ name: "enrol", ticket: res.ticket, secret: setup.secret, qr: setup.qr });
      } else {
        setStage({ name: "code", ticket: res.ticket });
      }
    });
  };

  const submitCode = (event: Event): void => {
    event.preventDefault();
    if (stage.name !== "code") return;
    void run(async () => {
      const res = useRecovery ? await api.verify(stage.ticket, undefined, code) : await api.verify(stage.ticket, code);
      onSignedIn(res.operator);
    });
  };

  const submitForgot = (event: Event): void => {
    event.preventDefault();
    void run(async () => {
      await api.forgotPassword(email.trim());
      // The same words whether or not the address is anyone's: the server
      // does not say, and neither may this screen.
      setStage({ name: "forgot", sent: true });
    });
  };
  const submitEnrolment = (event: Event): void => {
    event.preventDefault();
    if (stage.name !== "enrol") return;
    void run(async () => {
      const res = await api.confirmTwoFactor(stage.ticket, code);
      setStage({ name: "codes", codes: res.recoveryCodes });
    });
  };

  return (
    <div class="auth">
      <div class="card">
        <div class="lang-switch" role="group" aria-label={t.language}>
          {(["tr", "en"] as const).map((l) => (
            <button key={l} aria-pressed={lang === l} onClick={() => onLang(l)}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        {stage.name === "password" ? (
          <form onSubmit={submitPassword}>
            <h1>{t.title}</h1>
            <p class="sub">{t.sub}</p>

            <label for="email">{t.email}</label>
            <input id="email" type="email" autocomplete="username" value={email} onInput={(e) => setEmail(e.currentTarget.value)} required />

            <label for="password">{t.password}</label>
            <input
              id="password"
              type="password"
              autocomplete="current-password"
              value={password}
              onInput={(e) => setPassword(e.currentTarget.value)}
              required
            />

            <button class="btn primary" disabled={busy}>
              {busy ? t.checking : t.continue}
            </button>

            <button
              type="button"
              class="link-btn"
              onClick={() => {
                setError(null);
                setStage({ name: "forgot", sent: false });
              }}
            >
              {t.forgot}
            </button>
          </form>
        ) : null}

        {stage.name === "forgot" ? (
          stage.sent ? (
            <div>
              <h1>{t.forgotTitle}</h1>
              <div class="alert" role="status">
                {t.forgotSent}
              </div>
              <button type="button" class="btn" onClick={() => setStage({ name: "password" })}>
                {t.backToLogin}
              </button>
            </div>
          ) : (
            <form onSubmit={submitForgot}>
              <h1>{t.forgotTitle}</h1>
              <p class="sub">{t.forgotHint}</p>

              <label for="forgot-email">{t.email}</label>
              <input
                id="forgot-email"
                type="email"
                autocomplete="username"
                value={email}
                onInput={(e) => setEmail(e.currentTarget.value)}
                required
                autofocus
              />

              <button class="btn primary" disabled={busy || !email.trim()}>
                {busy ? t.forgotSending : t.forgotSubmit}
              </button>
              <button
                type="button"
                class="link-btn"
                onClick={() => {
                  setError(null);
                  setStage({ name: "password" });
                }}
              >
                {t.backToLogin}
              </button>
            </form>
          )
        ) : null}
        {stage.name === "code" ? (
          <form onSubmit={submitCode}>
            <h1>{t.twoFactorTitle}</h1>
            <p class="sub">{useRecovery ? t.recoveryHint : t.codeHint}</p>

            <label for="code">{useRecovery ? t.recoveryCode : t.code}</label>
            <input
              id="code"
              // Not type="number": a leading zero matters and spinners do not
              // belong on a one-time code.
              inputMode={useRecovery ? "text" : "numeric"}
              autocomplete="one-time-code"
              value={code}
              onInput={(e) => setCode(e.currentTarget.value)}
              required
              autofocus
            />

            <button class="btn primary" disabled={busy}>
              {busy ? t.verifying : t.signIn}
            </button>

            <button
              type="button"
              class="btn sm"
              style="width:100%;margin-top:8px"
              onClick={() => {
                setUseRecovery(!useRecovery);
                setCode("");
              }}
            >
              {useRecovery ? t.useApp : t.noPhone}
            </button>
          </form>
        ) : null}

        {stage.name === "enrol" ? (
          <form onSubmit={submitEnrolment}>
            <h1>{t.enrolTitle}</h1>
            <p class="sub">{t.enrolWhy}</p>

            <img class="qr" src={stage.qr} alt="" width="220" height="220" />
            <p class="sub">
              {t.qrFallback} <code>{stage.secret}</code>
            </p>

            <label for="enrol-code">{t.appCode}</label>
            <input
              id="enrol-code"
              inputMode="numeric"
              autocomplete="one-time-code"
              value={code}
              onInput={(e) => setCode(e.currentTarget.value)}
              required
              autofocus
            />

            <button class="btn primary" disabled={busy}>
              {busy ? t.verifying : t.finishSetup}
            </button>
          </form>
        ) : null}

        {stage.name === "codes" ? (
          <div>
            <h1>{t.codesTitle}</h1>
            <p class="sub">{t.codesHint}</p>
            <div class="snippet">{stage.codes.join("\n")}</div>
            <button
              class="btn primary"
              onClick={() => {
                void api.me().then((res) => onSignedIn(res.operator));
              }}
            >
              {t.codesDone}
            </button>
          </div>
        ) : null}

        {error ? <div class="alert error">{error}</div> : null}
      </div>
    </div>
  );
}
